import React from 'react';
import { getVehicle } from '../api/vehicles';
import { getLatestTelemetry } from '../api/telemetry';
import { getActiveDiagnostics } from '../api/diagnostics';
import useRoxFlag from '../hooks/useRoxFlag';

export default function VehicleDetail({ vehicleId, onBack }) {
  const showTelemetry = useRoxFlag('showTelemetry');
  const showDiagnostics = useRoxFlag('showDiagnostics');
  const [vehicle, setVehicle] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [telemetry, setTelemetry] = React.useState(null);
  const [diagnostics, setDiagnostics] = React.useState([]);
  const [telemLoading, setTelemLoading] = React.useState(true);
  const [diagsLoading, setDiagsLoading] = React.useState(true);
  const [telemError, setTelemError] = React.useState(null);
  const [diagsError, setDiagsError] = React.useState(null);

  React.useEffect(() => {
    async function loadVehicle() {
      try {
        const data = await getVehicle(vehicleId);
        setVehicle(data);
      } catch (err) {
        setError(err.message || 'Failed to load vehicle');
      } finally {
        setLoading(false);
      }
    }

    if (vehicleId) {
      loadVehicle();
    }
  }, [vehicleId]);

  React.useEffect(() => {
    async function loadTelemetry() {
      try {
        const data = await getLatestTelemetry(vehicleId);
        setTelemetry(data);
        setTelemError(null);
      } catch (err) {
        // Telemetry optional - don't fail the page
        console.error('Failed to load telemetry:', err);
        setTelemError(err.message || 'Unable to load telemetry data');
      } finally {
        setTelemLoading(false);
      }
    }

    async function loadDiagnostics() {
      try {
        const data = await getActiveDiagnostics(vehicleId);
        setDiagnostics(data || []);
        setDiagsError(null);
      } catch (err) {
        // Diagnostics optional - don't fail the page
        console.error('Failed to load diagnostics:', err);
        setDiagsError(err.message || 'Unable to load diagnostics data');
      } finally {
        setDiagsLoading(false);
      }
    }

    if (vehicleId) {
      loadTelemetry();
      loadDiagnostics();
    }
  }, [vehicleId]);

  if (loading) {
    return <div className="loading">Loading vehicle details...</div>;
  }

  if (error) {
    return (
      <div className="vehicle-detail">
        <button className="back-button" onClick={onBack}>← Back to Vehicles</button>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="vehicle-detail">
        <button className="back-button" onClick={onBack}>← Back to Vehicles</button>
        <div className="empty-state">Vehicle not found</div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="vehicle-detail">
      <button className="back-button" onClick={onBack}>← Back to Vehicles</button>
      <h2>{vehicle.make} {vehicle.model} ({vehicle.year})</h2>
      <div className="detail-grid">
        <div className="detail-item">
          <label>VIN</label>
          <div className="value" style={{ fontFamily: 'monospace' }}>{vehicle.vin}</div>
        </div>
        <div className="detail-item">
          <label>Status</label>
          <div className="value">
            <span className={`badge status-${vehicle.status}`}>{vehicle.status}</span>
          </div>
        </div>
        <div className="detail-item">
          <label>Location</label>
          <div className="value">{vehicle.location}</div>
        </div>
        <div className="detail-item">
          <label>Mileage</label>
          <div className="value">{vehicle.mileage.toLocaleString()} miles</div>
        </div>
        <div className="detail-item">
          <label>Fleet ID</label>
          <div className="value">{vehicle.fleet_id || 'Unassigned'}</div>
        </div>
        <div className="detail-item">
          <label>Last Service</label>
          <div className="value">{formatDate(vehicle.last_service)}</div>
        </div>
        <div className="detail-item">
          <label>Next Service</label>
          <div className="value">{formatDate(vehicle.next_service)}</div>
        </div>
        <div className="detail-item">
          <label>Created</label>
          <div className="value">{formatDate(vehicle.created_at)}</div>
        </div>
        <div className="detail-item">
          <label>Last Updated</label>
          <div className="value">{formatDate(vehicle.updated_at)}</div>
        </div>
      </div>

      {/* Telemetry Section */}
      {showTelemetry && (
        <>
          <h3 style={{ marginTop: '2rem' }}>Latest Telemetry</h3>
          {telemLoading ? (
        <div className="loading">Loading telemetry...</div>
      ) : telemError ? (
        <div className="error">⚠️ {telemError}</div>
      ) : telemetry ? (
        <div className="detail-grid">
          {telemetry.speed != null && (
            <div className="detail-item">
              <label>Speed</label>
              <div className="value">{telemetry.speed.toFixed(1)} mph</div>
            </div>
          )}
          {telemetry.battery_soc != null && (
            <div className="detail-item">
              <label>Battery SOC</label>
              <div className="value">{telemetry.battery_soc.toFixed(1)}%</div>
            </div>
          )}
          {telemetry.connectivity_status && (
            <div className="detail-item">
              <label>Connectivity</label>
              <div className="value">
                <span className={`badge status-${telemetry.connectivity_status}`}>
                  {telemetry.connectivity_status}
                </span>
              </div>
            </div>
          )}
          {telemetry.range_estimate != null && (
            <div className="detail-item">
              <label>Range Estimate</label>
              <div className="value">{telemetry.range_estimate} miles</div>
            </div>
          )}
          {telemetry.gps_lat != null && telemetry.gps_lon != null && (
            <div className="detail-item">
              <label>GPS Location</label>
              <div className="value">{telemetry.gps_lat.toFixed(4)}, {telemetry.gps_lon.toFixed(4)}</div>
            </div>
          )}
          {telemetry.odometer != null && (
            <div className="detail-item">
              <label>Odometer</label>
              <div className="value">{telemetry.odometer.toLocaleString()} miles</div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">No telemetry data available</div>
      )}
        </>
      )}

      {/* Diagnostics Section */}
      {showDiagnostics && (
        <>
          <h3 style={{ marginTop: '2rem' }}>Active Diagnostics</h3>
          {diagsLoading ? (
        <div className="loading">Loading diagnostics...</div>
      ) : diagsError ? (
        <div className="error">⚠️ {diagsError}</div>
      ) : diagnostics.length > 0 ? (
        <div style={{ marginTop: '1rem' }}>
          {diagnostics.map((diag) => (
            <div
              key={diag.id}
              style={{
                padding: '1rem',
                marginBottom: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: diag.severity === 'critical' ? '#fff5f5' : diag.severity === 'warning' ? '#fffbf0' : '#f9f9f9'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>{diag.code}</strong>
                <span className={`badge status-${diag.severity}`}>{diag.severity}</span>
              </div>
              <div>{diag.summary}</div>
              {diag.recommended_action && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                  <strong>Action:</strong> {diag.recommended_action}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state" style={{ color: '#22c55e', fontWeight: '500' }}>
          ✅ All systems normal - no active issues detected
        </div>
      )}
        </>
      )}
    </div>
  );
}
